<template>
  <ModalConsulte ref="modal" :hide-footer="false" modalWidth="720px">
    <template #header
      ><h4 class="modal-title">Comprovante de {{ titulosComprovante[tipo] }}</h4></template
    >
    <template #content>
      <p v-if="carregando" role="status">Carregando os dados gravados…</p>
      <p v-if="erro" class="alert alert-danger" role="alert">{{ erro }}</p>
      <template v-if="dados">
        <label for="papel-comprovante" class="form-label">Tipo de impressora / papel</label>
        <select
          id="papel-comprovante"
          v-model="papel"
          class="form-select mb-2"
          @change="pronto = false"
        >
          <option value="58">Térmica — bobina de 58 mm</option>
          <option value="80">Térmica — bobina de 80 mm</option>
          <option value="A4">Impressora comum — A4</option>
        </select>
        <p class="small text-muted">
          Na impressão, selecione a impressora e o papel correspondente, use escala de 100% e
          desative cabeçalhos e rodapés. Para a bobina, habilite o corte automático no driver da
          impressora.
        </p>
        <iframe
          ref="previa"
          :srcdoc="html"
          title="Pré-visualização do comprovante"
          sandbox="allow-same-origin allow-modals"
          class="w-100 border"
          style="height: 420px"
          @load="pronto = true"
        ></iframe>
      </template>
    </template>
    <template #actions>
      <button type="button" class="btn btn-light" @click="fechar">Fechar</button>
      <button v-if="erro" type="button" class="btn btn-secondary" @click="carregar">
        Tentar novamente
      </button>
      <button
        type="button"
        class="btn btn-primary"
        :disabled="!dados || !pronto || carregando"
        @click="imprimir"
      >
        Imprimir
      </button>
    </template>
  </ModalConsulte>
</template>

<script lang="ts">
  import { defineComponent } from 'vue';
  import ModalConsulte from '@/components/modal/ModalConsulte.vue';
  import BibEmprestimoService from '@/services/biblioteca/bibemprestimo/bibemprestimo.service';
  import BibExemplarService from '@/services/biblioteca/bibexemplar/bibexemplar.service';
  import BibDocumentoService from '@/services/biblioteca/bibdocumento/bibdocumento.service';
  import BibDocumentoAutorService from '@/services/biblioteca/bibdocumento/bibdocumentoautorservice';
  import BibAutorService from '@/services/biblioteca/bibautor/bibautor.service';
  import BibUsuarioService from '@/services/biblioteca/bibusuario/bibusuario.service';
  import { FiltrosModel } from '@/datasources/consulte-grid';
  import DadosLocais from '@/services/helper/storage';
  import EscolaService from '@/services/administracao/escola.service';
  import {
    DadosComprovante,
    PapelComprovante,
    TipoComprovante,
    montarComprovante,
    titulosComprovante,
  } from './comprovante';

  export default defineComponent({
    components: { ModalConsulte },
    data() {
      return {
        dados: null as DadosComprovante | null,
        tipo: 'emprestimo' as TipoComprovante,
        papel: '58' as PapelComprovante,
        id: 0,
        versao: 0,
        carregando: false,
        pronto: false,
        erro: '',
        titulosComprovante,
      };
    },
    computed: {
      html(): string {
        return this.dados ? montarComprovante(this.dados, this.tipo, this.papel) : '';
      },
    },
    beforeUnmount() {
      this.versao++;
    },
    watch: {
      '$route.fullPath'() {
        this.fechar();
        this.dados = null;
      },
    },
    methods: {
      open(id: number, tipo: TipoComprovante) {
        this.id = id;
        this.tipo = tipo;
        (this.$refs.modal as InstanceType<typeof ModalConsulte>).open();
        this.carregar();
      },
      fechar() {
        this.versao++;
        (this.$refs.modal as InstanceType<typeof ModalConsulte>).close();
      },
      async carregar() {
        const versao = ++this.versao;
        this.dados = null;
        this.erro = '';
        this.carregando = true;
        this.pronto = false;
        try {
          const emprestimo = await BibEmprestimoService.get(this.id);
          if (
            Number(emprestimo.segUsuarioId) !== Number(this.$route.params.segUsuarioId) ||
            Number(emprestimo.bibTipoUsuarioId) !== Number(this.$route.params.bibTipoUsuarioId)
          ) {
            throw new Error('O empréstimo não pertence ao usuário selecionado.');
          }
          const filtros = ['segUsuarioId', 'bibTipoUsuarioId'].map((campo) => {
            const filtro = new FiltrosModel(campo);
            filtro.addFilter(
              campo,
              String(
                campo === 'segUsuarioId' ? emprestimo.segUsuarioId : emprestimo.bibTipoUsuarioId,
              ),
              'pt-Br',
              'equals',
              'equals',
              campo,
            );
            return filtro;
          });
          const [exemplar, usuarios] = await Promise.all([
            BibExemplarService.get(Number(emprestimo.bibExemplarId)),
            BibUsuarioService.dadosIndexPaginado(
              0,
              1,
              filtros,
              Number(emprestimo.gesEscolaId || 0),
            ),
          ]);
          const [documento, vinculos] = await Promise.all([
            BibDocumentoService.get(Number(exemplar.bibDocumentoId)),
            BibDocumentoAutorService.get(Number(exemplar.bibDocumentoId)),
          ]);
          const autores = await Promise.all(
            (vinculos?.bibAutorId ?? []).map((id) => BibAutorService.get(id)),
          );
          const escola = DadosLocais.getInstance().getEscolaSelecionadaData();
          const codigoEscola = Number(emprestimo.gesEscolaId || 0);
          const nomeEscola = !codigoEscola
            ? ''
            : Number(escola.id) === codigoEscola && escola.nome
              ? escola.nome
              : (await EscolaService.get(codigoEscola)).nome;
          const dados: DadosComprovante = {
            emprestimo,
            usuario:
              usuarios.data.rows?.find(
                (u) => Number(u.segUsuarioId) === Number(emprestimo.segUsuarioId),
              )?.nome || `Usuário ${emprestimo.segUsuarioId}`,
            biblioteca: !codigoEscola
              ? 'Biblioteca Central'
              : `Biblioteca Escola ${nomeEscola || codigoEscola}`,
            titulo: `${documento.titulo}${documento.subTitulo ? '-' + documento.subTitulo : ''}`,
            autor: autores.map((a) => [a.sobreNome, a.nome].filter(Boolean).join(', ')).join('; '),
            tombamento: exemplar.identificacao || String(exemplar.bibExemplarId),
          };
          montarComprovante(dados, this.tipo, this.papel);
          if (versao === this.versao) this.dados = dados;
        } catch (erro) {
          if (versao === this.versao)
            this.erro =
              erro instanceof Error
                ? erro.message
                : 'Não foi possível carregar o comprovante. Tente novamente.';
        } finally {
          if (versao === this.versao) this.carregando = false;
        }
      },
      imprimir() {
        const iframe = this.$refs.previa as HTMLIFrameElement;
        const janela = iframe?.contentWindow;
        if (!janela || !this.pronto || !this.dados) return;
        // Uma página com a altura do recibo evita avançar uma folha longa na bobina.
        if (this.papel !== 'A4') {
          const altura =
            Math.ceil(
              ((janela.document.querySelector('.recibo')?.getBoundingClientRect().height || 600) *
                25.4) /
                96,
            ) + 2;
          const estilo = janela.document.getElementById('papel');
          if (estilo)
            estilo.textContent = `@page { size: ${this.papel}mm ${altura}mm; margin: 0; }`;
        }
        janela.focus();
        janela.print();
      },
    },
  });
</script>
